import { FIQAssist } from "../../app";
import "@fiqas/utils";
import {Component, Inject, Input, Output, Attribute, OutputArgs} from '@fiqas/angular-utils';
import "tinymce"
import { tinymce as tinyMCE } from "tinymce"
import "angular-ui-tinymce"
import { Utils } from "@fiqas/utils";
import { DialogService } from "../../services/dialogService";
import { CaseFileData } from '../../models/file';
import { FileReaderService } from '../../services/fileReaderService';
import { retry } from "rxjs/operator/retry";

interface imageData { blob: () => Blob, base64: () => string, filename: () => string, id: () => string };
interface imageMeta { alt?: string, constrain?: boolean, fileTye?: string, height?: string, width?: string, src?: string };
export type imageType = "jpeg" | "png";

export class TextEditorCommands {
    public static CursorAtEnd = "CursorAtEnd";
}

class EditorButton {
    text?: string;
    type?: string;
    title?: string;
    tooltip?: string;
    icon?: string;
    values?: any;
    fixedWidth?: boolean;
    cmd?: string;
    onPostRender?: () => void;
    onclick?: (event: tinyMCE.Events.Event & { control: tinyMCE.ui.Control }) => void;
    onselect?: (event: tinyMCE.Events.Event & { control: tinyMCE.ui.Control }) => void;
    instance?: tinyMCE.ui.Control
}

@Component({
    module: FIQAssist,
    selector: TextEditor.selector,
    template: `<div style="width:100%;height:100%;position:relative" class="editorcontainer ng-class:{isfocus:$ctrl.isFocus, 'has-value':$ctrl.hasValue, error:$ctrl.showError, readonly:!$ctrl.editable}">
                    <div id="editorToolbar-{{$id}}" class="editorToolbar" style="width:100%;"></div>
                    <div class="hint" style="position:absolute;left:0px;top:40px" ng-hide="$ctrl.hasValue">{{::$ctrl.hint}}</div>
                    <div ui-tinymce="$ctrl.options" class="editor" ng-model="$ctrl.internalValue" ng-focus="$ctrl.onFocus($event)" ng-blur="$ctrl.onBlur($event)" style="overflow: auto;overflow-y:scroll;position: absolute;top: 40px;left: 0px;right: 0px;bottom: 0px;"></div>
                    <span class="mandatory-dot" ng-if="$ctrl.mandatory" uib-tooltip="{{'FIELD_REQUIRED_TOOLTIP' | translate}}" tooltip-placement="bottom" tooltip-class="customTooltipTB"></span>
                <div>`
})
export class TextEditor {
    public static selector = "textEditor";
    private editor: tinyMCE.Editor;
    private isFocus: boolean;
    private wasFocused = false;
    private _internalValue: string;
    private _editable;

    @Input()
    public get editable() {
        return this._editable && !this._readOnly;
    }
    @Input() private maxImageWidth: number;
    @Input() private maxImageHeight: number;


    private get internalValue() {
        return this._internalValue;
    }
    private set internalValue(value: string) {
        const old = this._internalValue;
        this._internalValue = value;
        if (old !== this._internalValue) {
            this.onValueChanged({ value: this._internalValue });
        }
    }

    @Input() public get value() {
        return this.internalValue;
    }

    public set value(value: string) {
        this.internalValue = value;
    }

    @Output() public onValueChanged: ((params: { value: string }) => void);
    @Output() public onfileadded: OutputArgs<{ file: CaseFileData }>;

    constructor(
        @Inject('$scope') $scope: angular.IScope,
        @Inject('$window') private $window: angular.IWindowService,
        @Inject('') private dialogService: DialogService,
        @Inject('AttachmentLimit') private attachmentLimit: number,
        @Inject('fileReaderService') private fileReaderService: FileReaderService,
    ) {
        this.editable = true;
        this.onSetup = this.onSetup.bind(this);
        this.onImage = this.onImage.bind(this);
        this.onFilePicker = this.onFilePicker.bind(this);
        this.options = {
            plugins: "image paste",
            skin_url: "/tinymce/lightgray",
            setup: this.onSetup,
            statusbar: false,
            browser_spellcheck: true,
            convert_urls: false,
            element_format: "html",
            inline: true,
            insert_toolbar: 'quickimage',
            fixed_toolbar_container: "#editorToolbar-" + $scope.$id,
            images_upload_handler: this.onImage,
            menubar: false,
            toolbar: `undo redo | btn-bold btn-italic btn-underline btn-strikethrough |
                            btn-alignleft btn-aligncenter btn-alignright | btn-outdent btn-indent | btn-removeformat | btn-fontselect btn-fontsizeselect | btn-image`,
            file_picker_types: "image",
            file_picker_callback: this.onFilePicker
        };
        this.options['paste_data_images'] = true;
        $scope.$on(TextEditorCommands.CursorAtEnd, () => setTimeout(() => this.cursorAtEnd(), 250));
    }

    private setStateHandler(name: string, button: EditorButton) {
        var editor: any = this.editor;
        return function (this: tinyMCE.ui.Control) {
            var btn = this;
            if (editor.formatter) {
                editor.formatter.formatChanged(name, (state) => btn.active(state));
            } else {
                editor.on('init', () => editor.formatter.formatChanged(name, (state) => btn.active(state)));
            }
        };
    }

    private stateButtons: { [name: string]: EditorButton } = {
        'bold': {
            tooltip: "Bold",
            icon: "bold",
            cmd: "Bold"
        },
        'italic': {
            tooltip: "Italic",
            icon: "italic",
            cmd: "Italic"
        },
        'underline': {
            tooltip: "Underline",
            icon: "underline",
            cmd: "Underline"
        },
        'strikethrough': {
            tooltip: "Strikethrough",
            icon: "strikethrough",
            cmd: "Strikethrough"
        },
        'alignleft': {
            tooltip: "Align left",
            icon: "alignleft",
            cmd: "JustifyLeft"
        },
        'aligncenter': {
            tooltip: "Align center",
            icon: "aligncenter",
            cmd: "JustifyCenter"
        },
        'alignright': {
            tooltip: "Align right",
            icon: "alignright",
            cmd: "JustifyRight"
        }
    };

    private commandButtons: { [name: string]: EditorButton } = {
        'outdent': {
            tooltip: "Decrease indent",
            icon: "outdent",
            cmd: "Outdent"
        },
        'indent': {
            tooltip: "Increase indent",
            icon: "indent",
            cmd: "Indent"
        },
        'removeformat': {
            tooltip: "Remove formatting",
            icon: "removeformat",
            cmd: "RemoveFormat"
        },
        'image': {
            tooltip: "Insert/Edit image",
            icon: "image",
            cmd: "mceImage"
        }
    };

    private buttons: { [name: string]: EditorButton } = {};

    private onSetup(editor: tinyMCE.Editor) {
        this.editor = editor;
        Utils.forEachProperty<EditorButton>(this.stateButtons, (p, k) => {
            p.onPostRender = this.setStateHandler(k, p);
            this.buttons[k] = p;
        });
        Utils.forEachProperty<EditorButton>(this.commandButtons, (p, k) => this.buttons[k] = p);

        var { key, button } = createFontSelectButton(editor);
        this.buttons[key] = button;

        var { key, button } = createFontSizeButton(editor);
        this.buttons[key] = button;

        let buttons = this.buttons;
        const t = this;
        Utils.forEachProperty<EditorButton>(this.buttons,
            (p, k) => {
                var pr = p.onPostRender;
                p.onPostRender = function (this: tinyMCE.ui.Control) {
                    buttons[k].instance = this;
                    this.disabled(t.editable !== undefined && !t.editable);
                    if (pr) {
                        pr.apply(this);
                    }
                };
                editor.addButton('btn-' + k, p);
            });

        setTimeout(() => {
            try {
                if (this.editable !== undefined && !this.editable)
                    this.editor.setMode('readonly');
                else
                    this.editor.setMode('design');
            } catch (e) {
                //this sometimes fails, but that's ok.
            }
        }, 1000);
    }

    private cursorAtEnd() {
        this.editor.focus(false);
        this.editor.selection.select(this.editor.getBody(), true);
        this.editor.selection.collapse(false);
        this.editor.execCommand('mceInsertContent', false, "");
    }

    private options: tinyMCE.Settings;

    private stripRichTextRegex: any = /(?!.img.*src.+)(<([^>]+)>)|&nbsp;|\s/ig;
    private get hasValue() {
        return this.value !== null && this.value !== undefined && this.value.replace(this.stripRichTextRegex, "") !== "";
    }
    private get showError() {
        return this.mandatory && !this.isFocus && this.wasFocused && !this.hasValue;
    }

    private _mandatory;
    @Attribute() public get mandatory() {
        return this._mandatory;
    }
    public set mandatory(val: boolean | string) {
        this._mandatory = val === true || val === "true";
    }

    private _hint: string;
    @Attribute() public get hint() {
        return this._hint || "";
    };

    public set hint(val: string) {
        this._hint = val || "";
    }

    public onFocus(event) {
        this.wasFocused = this.isFocus = true;
    }

    public onBlur(event) {
        this.isFocus = false;
    }

    public onFilePicker(callback: (uri: string, meta: imageMeta) => {}, value, meta: imageMeta) {
        let input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = () => {
            const file = input.files[0];
            if (!this.fileSizeAllowed(file.size)) {
                this.fileSizeTooLarge(file);
            } else {
                const reader = new FileReader();
                reader.onload = (ev: Event) => {
                    const id = 'blobid' + (new Date()).getTime();
                    const res = ev.target['result'];
                    const img = new Image();
                    img.onload = () => {
                        if (!this.imageDimensionsAllowed(img, this.maxImageWidth, this.maxImageHeight)) {
                            this.imageTooLarge(file);
                        } else {
                            const data = resizeImage(img, this.maxImageWidth, this.maxImageHeight, "png");
                            const bCache = this.editor['editorUpload'].blobCache;
                            const bInfo = bCache.create(id, dataURItoBlob(data.imgres));
                            bCache.add(bInfo);
                            const uri = bInfo.blobUri();
                            callback(uri, { alt: file.name, src: uri });
                        }
                    };
                    img.src = res;
                };
                reader.readAsDataURL(file);
            }
            this.$window.document.body.removeChild(input);
        };
        this.$window.document.body.appendChild(input);
        input.click();
    }

    private closeTinymceDialog(): void {
        if (tinymce && tinymce.activeEditor && tinymce.activeEditor.windowManager) {
            tinymce.activeEditor.windowManager.close();
        }
    }

    private fileSizeTooLarge(file: File) : void {
        const filename = (file.name) ? file.name : 'image.png';

        this.closeTinymceDialog();
        this.dialogService.showErrorDialog("warning", 'ERROR', 'FILES_EXCEEDS_LIMIT', {
        messageArgs: [this.attachmentLimit.toFixed(2), filename],
        translate: true
        });
    }

    private imageTooLarge(file: File): void {
        const imageSizeMessage = (file.name) ? 'IMAGE_TO_LARGE' : 'IMAGE_TO_LARGE_NO_FILE_NAME';
        const fileName = (file.name) ? file.name : 'image.png';
        const fileDescription = (file.name) ? file.name : 'image.png';

        this.closeTinymceDialog();
        let that = this;
        const dimensionArgs = this.maxImageWidth + ' x ' + this.maxImageHeight;
        this.dialogService.showConfirmationDialog("warning", "IMAGE_TO_LARGE_TITLE", imageSizeMessage, "ADD_ATTACHMENT_BUTTON", null,
            {messageArgs: [file.name, dimensionArgs], translate: true})
            .then(() => {
                if (!this.fileSizeAllowed(file.size)) {
                    this.dialogService.showErrorDialog("warning", 'ERROR', 'FILES_EXCEEDS_LIMIT', {
                        messageArgs: [this.attachmentLimit.toFixed(2), fileName],
                        translate: true
                    });
                } else {
                    this.fileReaderService.ReadAsBinaryString(file).then(y => {
                        let caseFile: CaseFileData = <CaseFileData>{
                            filename: fileName,
                            description: fileDescription,
                            content: btoa(<string>y)
                        };
                        this.newAttachment(caseFile);
                    });
                }
                that.removeEmptyImageTags();
            }, function () {
                that.removeEmptyImageTags();
            });
    }

    private removeEmptyImageTags() {
        let imgRegex: any = /<img>/ig;
        if (this.value && this.value.indexOf('<img>') > -1) {
            this.value = this.value.replace(imgRegex, "");
        }
        if (this.editor && this.editor['targetElm'] && this.editor['targetElm'].innerHTML &&
            this.editor['targetElm'].innerHTML.indexOf('<img>') > -1) {
            this.editor['targetElm'].innerHTML = this.editor['targetElm'].innerHTML.replace(imgRegex, "");
        }
    }

    private onImage(fileDropped: any, callback: (url: string) => {}, error: (error: string) => {}) {
        const file = fileDropped.blob();
        if (!this.fileSizeAllowed(file.size)) {
            this.fileSizeTooLarge(file);
        } else {
            const reader = new FileReader();
            reader.onload = (ev: Event) => {
                const res = ev.target["result"];
                const img = new Image();
                img.onload = () => {
                    if (!this.imageDimensionsAllowed(img, this.maxImageWidth, this.maxImageHeight)) {
                        this.imageTooLarge(file);
                        callback(null);
                    } else {
                        callback(img.src);
                    }
                };
                img.src = res;
            };
            reader.readAsDataURL(file);
        }
    }

    public newAttachment(fileData: CaseFileData) {
        this.onfileadded({ file: fileData });
    }

    private imageDimensionsAllowed(img: HTMLImageElement, maxWidth: number, maxHeight: number): boolean {
        return (img.width <= maxWidth && img.height <= maxHeight);
    }

    private fileSizeAllowed(imgSize: number): boolean {
        return (imgSize <= this.attachmentLimit * 1024 * 1024);
    }

    private _readOnly: boolean = false;
    @Input() public get readOnly(): boolean { return this._editable }
    public set readOnly(val: boolean) {
        if (val != this._readOnly) {
            this._readOnly = val;
            if (val && this.editor) {
                this.editor.setMode('readonly');
            }
        }
    }

    public set editable(value: boolean) {
        this._editable = value;
        if (this.editor) {
            if (!this.editable)
                this.editor.setMode('readonly');
            else
                this.editor.setMode('design');
        }
        Utils.forEachProperty<EditorButton>(this.buttons, (b, k) => b.instance.disabled(!value), (b, k) => b.instance != undefined);
    }
}

function createFontSizeButton(editor: tinyMCE.Editor): { key: string, button: EditorButton } {
    var fontsizeFormats = editor.settings['fontsizeFormats'] || '8pt 10pt 12pt 14pt 18pt 24pt 36pt';
    var items = fontsizeFormats.split(' ').map(item => {
        var text = item, value = item;
        var values = item.split('=');
        if (values.length > 1) {
            text = values[0];
            value = values[1];
        }
        return { text: text, value: value };
    });

    return {
        key: 'fontsizeselect',
        button: {
            type: 'listbox',
            text: 'Font Sizes',
            tooltip: 'Font Sizes',
            values: items,
            fixedWidth: true,
            onPostRender: function () {
                var btn = this;
                editor.on('nodeChange', function (e) {
                    var px = FontInfo.getFontSize(editor.getBody(), e.element);
                    var pt = FontInfo.toPt(px);
                    var current = items.filter(item => item.value === px || item.value === pt).shift();
                    var value = current ? current.value : null;
                    btn.value(value);
                    if (!value)
                        btn.text(pt);
                });
            },
            onclick: (e) => {
                if (e.control.settings['value']) {
                    editor.execCommand('FontSize', false, e.control.settings['value']);
                }
            }
        }
    }
}

function createFontSelectButton(editor: tinyMCE.Editor): { key: string, button: EditorButton } {
    const defaultFontsFormats = [
        { text: 'Andale Mono', value: 'andale mono,monospace' },
        { text: 'Arial', value: 'arial,helvetica,sans-serif' },
        { text: 'Arial Black', value: 'arial black,sans-serif' },
        { text: 'Book Antiqua', value: 'book antiqua,palatino,serif' },
        { text: 'Comic Sans MS', value: 'comic sans ms,sans-serif' },
        { text: 'Courier New', value: 'courier new,courier,monospace' },
        { text: 'Georgia', value: 'georgia,palatino,serif' },
        { text: 'Helvetica', value: 'helvetica,arial,sans-serif' },
        { text: 'Impact', value: 'impact,sans-serif' },
        { text: 'Symbol', value: 'symbol' },
        { text: 'Tahoma', value: 'tahoma,arial,helvetica,sans-serif' },
        { text: 'Terminal', value: 'terminal,monaco,monospace' },
        { text: 'Times New Roman', value: 'times new roman,times,serif' },
        { text: 'Trebuchet MS', value: 'trebuchet ms,geneva,sans-serif' },
        { text: 'Verdana', value: 'verdana,geneva,sans-serif' },
        { text: 'Webdings', value: 'webdings' },
        { text: 'Wingdings', value: 'wingdings,zapf dingbats' }
    ];
    var fonts = editor.settings.font_formats ?
        editor.settings.font_formats.replace(/;$/, '').split(';').map((v, i) => {
            var f = v.split("=");
            return { text: f[0], value: f[1] }
        }) : defaultFontsFormats;

    fonts.forEach(f => f['textStyle'] = f.value.indexOf('dings') == -1 ? `font-family:${f.value}` : '');

    return {
        key: 'fontselect',
        button: {
            type: 'listbox',
            text: 'Font Family',
            tooltip: 'Font Family',
            values: fonts,
            fixedWidth: true,
            onPostRender: function (this: tinyMCE.ui.Control) {
                var btn: any = this
                var getFirstFont = (fontFamily) => fontFamily ? fontFamily.split(',')[0] : '';
                editor.on('nodeChange', function (e) {
                    var fontFamily, value = null;
                    fontFamily = FontInfo.getFontFamily(editor.getBody(), e.element);
                    var current = fonts.filter(font => font.value.toLowerCase() == fontFamily.toLowerCase()).shift();
                    if (current)
                        value = current.value;
                    else
                        current = fonts.filter(font => getFirstFont(font.value).toLowerCase() == getFirstFont(fontFamily).toLowerCase()).shift();
                    if (current)
                        value = current.value;

                    btn.value(value);

                    if (!value && fontFamily) {
                        btn.text(getFirstFont(fontFamily));
                    }
                });
            },
            onselect: function (e) {
                if (e.control.settings['value']) {
                    editor.execCommand('FontName', false, e.control.settings['value']);
                }
            }
        }
    };
}

function dataURItoBlob(dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    var byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

    // write the bytes of the string to an ArrayBuffer
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    // write the ArrayBuffer to a blob, and you're done
    var bb = new Blob([ab], { type: mimeString });
    return bb;
}

function resizeImage(img: HTMLImageElement, maxWidth: number, maxHeight: number, imageType: imageType) {
    var resized = false;
    var width = maxWidth = maxWidth || img.width;
    var height = maxHeight = maxHeight || img.height;
    if (img.width > maxWidth || img.height > maxHeight) {
        var wratio = maxWidth / img.width;
        var hratio = maxHeight / img.height;
        if (hratio < wratio) {
            width = img.width * hratio;
            height = img.height * hratio;
        } else {
            width = img.width * wratio;
            height = img.height * wratio;
        }
        resized = true;
    } else {
        width = img.width;
        height = img.height;
    }

    var canvas = document.createElement("canvas")
    var ctx = canvas.getContext("2d");

    canvas.height = height;
    canvas.width = width;

    if (img.width > maxWidth || img.height > maxHeight) {
        var oc = document.createElement('canvas')
        var octx = oc.getContext('2d');

        oc.width = Math.round(img.width - (width / 2));
        oc.height = Math.round(img.height - (height / 2));
        octx.drawImage(img, 0, 0, oc.width, oc.height);
        octx.drawImage(oc, 0, 0, oc.width * 0.5, oc.height * 0.5);
        ctx.drawImage(oc, 0, 0, oc.width * 0.5, oc.height * 0.5, 0, 0, canvas.width, canvas.height);
    } else {
        ctx.drawImage(img, 0, 0, width, height);
    }
    return { imgres: canvas.toDataURL(`image/${imageType}`), resized: resized };
}

declare var tinymce;
var DOM: tinyMCE.dom.DOMUtils = tinymce.DOM;
class FontInfo {
    private static getSpecifiedFontProp = function (propName, rootElm, elm) {
        while (elm !== rootElm) {
            if (elm.style[propName]) {
                return elm.style[propName];
            }
            elm = elm.parentNode;
        }
        return '';
    };

    private static getComputedFontProp = (propName, elm) => DOM.getStyle(elm, propName, true);

    public static toPt = (fontSize) => /[0-9.]+px$/.test(fontSize) ? Math.round(parseInt(fontSize, 10) * 72 / 96) + 'pt' : fontSize;

    public static getFontSize = function (rootElm, elm) {
        var specifiedFontSize = FontInfo.getSpecifiedFontProp('fontSize', rootElm, elm);
        return specifiedFontSize !== '' ? specifiedFontSize : FontInfo.getComputedFontProp('fontSize', elm);
    };

    public static getFontFamily = function (rootElm, elm) {
        var specifiedFontSize = FontInfo.getSpecifiedFontProp('fontFamily', rootElm, elm);
        var fontValue = specifiedFontSize !== '' ? specifiedFontSize : FontInfo.getComputedFontProp('fontFamily', elm);

        return fontValue !== undefined ? fontValue.replace(/[\'\"]/g, '').replace(/,\s+/g, ',') : '';
    };
}
